import { useState } from "react";
import { Clock, KeyRound, LogOut, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { changePassword, formatApiError } from "@/lib/api";
import { TIME_ZONES } from "@/lib/time";

export const UserMenu = () => {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  if (!user) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-lg border border-slate-700/50 bg-slate-800/60 px-2.5 py-1.5 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-slate-700/60">
          <UserCircle2 className="h-4 w-4 text-blue-400" />
          <span className="max-w-[140px] truncate">{user.name || user.email}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 border-slate-700 bg-slate-900 text-slate-200">
          <DropdownMenuLabel className="text-xs">
            <p className="truncate">{user.email}</p>
            <p className="text-[10px] font-normal capitalize text-slate-500">{user.role}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-800" />
          <DropdownMenuItem data-testid="time-zone-menu-item" onSelect={() => setTzOpen(true)} className="text-xs"><Clock className="mr-2 h-3.5 w-3.5" />Time zone{user.timezone ? ` · ${user.timezone.split("/").pop().replace("_", " ")}` : ""}</DropdownMenuItem>
          <DropdownMenuItem data-testid="change-password-menu-item" onSelect={() => setPwOpen(true)} className="text-xs"><KeyRound className="mr-2 h-3.5 w-3.5" />Change password</DropdownMenuItem>
          <DropdownMenuItem data-testid="logout-menu-item" onSelect={logout} className="text-xs text-red-300"><LogOut className="mr-2 h-3.5 w-3.5" />Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <TimeZoneDialog open={tzOpen} onOpenChange={setTzOpen} current={user.timezone || ""} />
    </>
  );
};

const TimeZoneDialog = ({ open, onOpenChange, current }) => {
  const [tz, setTz] = useState(current);
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    api.put("/auth/me/timezone", { timezone: tz || null })
      .then(() => { toast.success("Time zone saved"); window.location.reload(); })
      .catch((e) => { toast.error(formatApiError(e)); setBusy(false); });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-zone-dialog" className="max-w-sm border-slate-700 bg-slate-900 text-slate-200">
        <DialogHeader><DialogTitle>Time zone</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Every page shows times in this zone. Browser time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}.</p>
        <select data-testid="time-zone-select" value={tz} onChange={(e) => setTz(e.target.value)} className="rounded-md border border-slate-700 bg-slate-950 px-2 py-2 text-sm text-slate-200">
          {TIME_ZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <DialogFooter>
          <Button data-testid="time-zone-save" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-500">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await changePassword(cur, next);
      toast.success("Password changed");
      setCur(""); setNext("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="border-slate-700 bg-slate-900 text-slate-200 sm:max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Current password</Label>
            <Input data-testid="current-password-input" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className="border-slate-700/60 bg-slate-800/50" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">New password (min 8 characters)</Label>
            <Input data-testid="new-password-input" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="border-slate-700/60 bg-slate-800/50" />
          </div>
          <DialogFooter>
            <Button data-testid="change-password-submit" type="submit" className="bg-blue-600 text-white hover:bg-blue-700">Update password</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
